/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { OLAPTransformerResult, useParameter, useDictionary, Column } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, tooltipPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { dictionaryService } from "../../../dataFlow";
import { arrayByLength, uniq } from "../../../../../shared";
import { last } from "lodash";

const CATEGORIES_AXIS = "calendar.default";
const SERIES_AXIS = "commodities.default";

export function useHighchartTransformer(olapData: OLAPTransformerResult | null): Highcharts.Options | undefined {
  const [state, setState] = useState<Highcharts.Options>();
  const [calendarIds] = useParameter<number[]>("@calendar");

  const [calendarItems] = useDictionary(dictionaryService, "calendar-origin");
  const olapColumns = useMemo(() => calendarIds?.map(id => olapData?.columns.find(e => e.selection[CATEGORIES_AXIS] === id)).filter(Boolean), [calendarIds, olapData]);
  const lastColumn = last(olapColumns || []);

  const сategories = useMemo(() => uniq(calendarIds || []).map(calendarId => {
    const column = olapColumns?.find(e => e?.selection[CATEGORIES_AXIS] === calendarId);
    return column?.headers.find(e => e.id === `${CATEGORIES_AXIS}.name`)?.name;
  }).filter(Boolean) as string[], [olapColumns, calendarItems]);

  const [methodId] = useParameter<number>("@method");
  const isMethodIndex = methodId === 2;
  // const colors = ['#feb340', '#ecec40', '#b3d940', '#40a040', '#d0d0d0', '#a0a040', '#65d9fe'];
  const colors = ['#0F8AC0', '#1BB8C2', '#1CA955', '#E38114', '#DD5C3D', '#8F8F8F', '#D9D10C', '#2D5EDA', '#784FBA', '#D656A3'];

  const allPoints = olapData?.rows.flatMap(row => row.points.map(point => Math.round(point.value))) || [0];
  const tickPositions = olapData?.rows.map((row) => {
    const lastPoint = row.points.find(point => point.column === lastColumn);
    return Math.round(lastPoint?.value || 0);
  });
  let minYAxis = Math.round((Math.min(...allPoints) - 50) / 100) * 100;
  if (minYAxis < 0) {
    minYAxis = 0;
  }
  const maxYAxis = Math.round((Math.max(...allPoints) + 50) / 100) * 100;
  const options = useChartOptions({
    tooltip: {
      useHTML: true,
      formatter: function () {
        const commodity = this.point.series.name;
        let absoluteUnit = 'USD/mt';
        switch (commodity) {
          case 'Crude Oil':
            absoluteUnit = 'USD/BBL';
            break;
          case 'Gold':
            absoluteUnit = 'USD';
            break;
          case 'DAX':
          case 'S&P 500':
            absoluteUnit = '';
            break;
          default:
            break;
        }
        const unit = isMethodIndex ? '%' : absoluteUnit;
        const calendar = this.point.category;
        const pointValue = Highcharts.numberFormat(this.point.y || 0, 0);
        return `<div>
            <b>${commodity}</b><br/>
            <b>${calendar}</b>: <span>${pointValue} ${unit}</span>
        </div>`;
      },
    },
  }, [
    chartPipe("spline"),
    legendPipe("bottom", {
      labelFormatter: function () {
        const { name } = this;
        return name;
      }
    }),
    addXAxisPipe(сategories),
    addYAxisPipe({
      min: minYAxis,
      gridLineColor: '#fff',
      max: maxYAxis,
      labels: {
        enabled: true,
        formatter: function () {
          var label = this.axis.defaultLabelFormatter.call(this);
          if (/^[0-9]{4}$/.test(label)) {
            return Highcharts.numberFormat(this.value, 0);
          }
          return label;
        },
      },
    }),
    addYAxisPipe({
      opposite: true,
      min: minYAxis,
      max: maxYAxis,
      tickPositions: [minYAxis, ...(tickPositions || []), maxYAxis],
      gridLineColor: '#fff',
      labels: {
        enabled: true,
        useHTML: true,
        formatter: function () {
          const value = this.value;
          if (tickPositions?.includes(value)) {
            const tickColor = colors[tickPositions.indexOf(value)];
            return `<span style="color: ${tickColor}">${Highcharts.numberFormat(value, 0)}</span>`;
          }
          return '';
        },
      },
    }),
  ]);

  useEffect(() => {
    if (olapData) {
      const series: SeriesOptionsType[] = olapData.rows.map((row, rowIndex) => {
        const serieId = row.attributes[`${SERIES_AXIS}.id`];
        let serieName = row.attributes[`${SERIES_AXIS}.name`][9];
        if (!isMethodIndex && serieName === 'Cotton') {
          serieName += '*';
        }
        const serieColor = colors[rowIndex];
        const serieData: number[] = uniq(calendarIds || []).map(calId => {
          const col = olapColumns?.find(e => e?.selection[CATEGORIES_AXIS] === calId);
          if (!col) {
            return undefined;
          }
          const colPoint = row.points.find(point =>
            point.column.selection[CATEGORIES_AXIS] === col?.selection[CATEGORIES_AXIS]
          );
          const colData: number | undefined = colPoint?.value;
          return colData || 0;
        }).filter(e => e !== undefined) as number[];

        return {
          type: "spline",
          yAxis: 0,
          xAxis: 0,
          color: serieColor,
          dashStyle: "Solid",
          lineWidth: 3,
          zIndex: 1,
          marker: {
            radius: 1,
          },
          dataLabels: {
            enabled: false,
          },
          showInLegend: true,
          states: {
            hover: {
              lineWidth: 0,
              lineWidthPlus: 0,
            },
          },
          name: serieName,
          id: serieId,
          data: serieData,
        }
      });
      const result: Highcharts.Options = {
        ...options,
        series: series,
      };

      setState(result);
    } else {
      setState(undefined);
    }
  }, [olapData, olapColumns, isMethodIndex]);
  return state;
}
